import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'
import {
    identifier,
    removeData,
    setIdentified,
    setRemoved,
    setStep,
    setSessions,
    setAddresses,
    setLikes,
    setRates,
    setSuccess as setProfileRemoveDataSuccess,
    setError as setProfileRemoveDataError,
    setErrorMessage as setProfileRemoveDataErrorMessage
} from '../../../../../store/reducers/ProfileRemoveDataSlice'
import {
    setResponseSuccess,
    setResponseSuccessShow,
    //setResponseWarning,
    //setResponseWarningShow,
    setResponseErrorShow,
    setResponseError
} from '../../../../../store/reducers/app/ResponseInfoSlices'
import { setAlert } from '../../../../../store/reducers/app/CustomAlertSlice'
import {
    SetPhoneInitialValues,
    SetPhoneOnsubmit,
    SetPhoneValidate
} from '../../../../../FormTypes/FSetPhone'
import {
    SetCodeInitialValues,
    SetCodeOnsubmit,
    SetCodeValidate
} from '../../../../../FormTypes/FSetCode'
import { FormikErrors, FormikHelpers } from 'formik'
import { RoutesPath } from '../../../../../constants/RoutesPath'
import { Strings } from '../../../../../constants/Strings'
import { Colors } from '../../../../../constants/Colors'
import style from '../../../../../rootStyles/Content.module.css'
import Header from '../../../../../components/Header/Header'
import Info from '../../../../../components/Info/Info'
import MiniSwitch from '../../../../../components/widgets/Switches/MiniSwitch'
import SendPhoneForm from '../../../../../components/forms/SendPhoneForm'
import SendCodeForm from '../../../../../components/forms/SendCodeForm'
import PrevNextChanger from '../../../../../components/widgets/PrevNextChanger/PrevNextChanger'

const RemoveData = () => {

    const dispatch = useAppDispatch()

    const theme = useAppSelector<string>(state => state.theme.theme)
    const language = useAppSelector<string>(state => state.language.language)

    const identified = useAppSelector<boolean>(state => state.profileRemoveData.identified)
    const removed = useAppSelector<boolean>(state => state.profileRemoveData.removed)

    const step = useAppSelector<number>(state => state.profileRemoveData.step)
    const sessions = useAppSelector<boolean>(state => state.profileRemoveData.sessions)
    const addresses = useAppSelector<boolean>(state => state.profileRemoveData.addresses)
    const likes = useAppSelector<boolean>(state => state.profileRemoveData.likes)
    const rates = useAppSelector<boolean>(state => state.profileRemoveData.rates)

    const profileRemoveDataSuccess = useAppSelector<number | null>(state => state.profileRemoveData.success)
    const profileRemoveDataError = useAppSelector<number | null>(state => state.profileRemoveData.error)
    const profileRemoveDataErrorMessage = useAppSelector<string | null>(state => state.profileRemoveData.errorMessage)

    const onChangeStep = (step: number) => {
        dispatch(setStep(step))
    }

    useEffect(() => {
        if (identified) {
            onChangeStep(3)
        }
    }, [identified])

    useEffect(() => {
        if (removed) {
            dispatch(setIdentified(false))
            dispatch(setRemoved(false))
            onChangeStep(1)
            dispatch(setSessions(false))
            dispatch(setAddresses(false))
            dispatch(setLikes(false))
            dispatch(setRates(false))
        }
    }, [removed])

    const onChangeSessions = () => {
        dispatch(setSessions(!sessions))
    }

    const onChangeAddresses = () => {
        dispatch(setAddresses(!addresses))
    }

    const onChangeLikes = () => {
        dispatch(setLikes(!likes))
    }

    const onChangeRates = () => {
        dispatch(setRates(!rates))
    }

    // SEND PHONE
    const identifierInitialValues: SetPhoneInitialValues = {
        code: '',
        phone: ''
    }

    const identifierValidate: SetPhoneValidate = (values: SetPhoneInitialValues) => {
        let errors: FormikErrors<SetPhoneInitialValues> = {}
        
        if (!values.phone) {
            errors.phone = Strings[language ?? 'tm'].required
        } else if (values.phone.length < 8) {
            errors.phone = Strings[language ?? 'tm'].min + ': 8/' + values.phone.length
        } else if (values.phone.length > 10) {
            errors.phone = Strings[language ?? 'tm'].max + ': 10/' + values.phone.length
        } else if (!/^[0-9]{8,10}$/i.test(values.phone)) {
            errors.phone = Strings[language ?? 'tm'].error + '. ' + Strings[language ?? 'tm'].pattern + ': ' + Strings[language ?? 'tm'].numbers + ' (0-9).'
        }
        return errors
    }

    const identifierPhoneOnSubmit: SetPhoneOnsubmit = (values: SetPhoneInitialValues, actions: FormikHelpers<SetPhoneInitialValues>) => {
        const phone = (values.code ? values.code : '+993') + ' ' + values.phone
        dispatch(identifier({
            phone: phone
        }))
        actions.setSubmitting(false);
    }

    // SEND CODE
    const removeDataInitialValues: SetCodeInitialValues = {
        code: ''
    }

    const removeDataValidate: SetCodeValidate = (values: SetCodeInitialValues) => {
        let errors: FormikErrors<SetCodeInitialValues> = {}
        
        if (!values.code) {
            errors.code = Strings[language ?? 'tm'].required
        } else if (values.code.length < 6) {
            errors.code = Strings[language ?? 'tm'].min + ': 6/' + values.code.length
        } else if (values.code.length > 6) {
            errors.code = Strings[language ?? 'tm'].max + ': 6/' + values.code.length
        } else if (!/^[0-9]{6,6}$/i.test(values.code)) {
            errors.code = Strings[language ?? 'tm'].error + '. ' + Strings[language ?? 'tm'].pattern + ': ' + Strings[language ?? 'tm'].numbers + ' (0-9).'
        }
        return errors
    }

    const removeDataOnSubmit: SetCodeOnsubmit = (values: SetCodeInitialValues, actions: FormikHelpers<SetCodeInitialValues>) => {
        const code = parseInt(values.code)
        dispatch(removeData({
            code: code,
            sessions: sessions,
            addresses: addresses,
            likes: likes,
            rates: rates
        }))
        actions.setSubmitting(false);
    }

    // ERRORS
    useEffect(() => {
        if (profileRemoveDataSuccess) {
            dispatch(setResponseSuccess(profileRemoveDataSuccess))
            dispatch(setResponseSuccessShow(true))
            dispatch(setProfileRemoveDataSuccess(null))
        }
    }, [profileRemoveDataSuccess])
    
    useEffect(() => {
        if (profileRemoveDataError) {
            dispatch(setResponseError(profileRemoveDataError))
            dispatch(setResponseErrorShow(true))
            dispatch(setProfileRemoveDataError(null))
        }
    }, [profileRemoveDataError])

    useEffect(() => {
        if (profileRemoveDataErrorMessage) {
            dispatch(setAlert({
                title: (
                    Strings[language ?? 'tm'].profileDataPage + "!"
                ),
                description: (
                    Strings[language ?? 'tm'].profileDataTrash + " | "
                    + Strings[language ?? 'tm'].error + ": "
                    + profileRemoveDataErrorMessage
                ),
                functions: [],
                sound: 0
            }))
            dispatch(setProfileRemoveDataErrorMessage(null))
        }
    }, [profileRemoveDataErrorMessage])

    return (
        <div
            className={style.container}
            style={{
                backgroundColor: Colors[theme ?? 'light'].background
            }}
        >
            <Header
                leftIcon={"fas fa-chevron-left"}
                leftPath={RoutesPath.privacyPolicy}
                leftClick={null}
                title={Strings[language ?? 'tm'].profileDataTrash}
                rightIcon={null}
                rightPath={null}
                rightClick={null}
            />
            <div
                className={style.content}
                style={{
                    backgroundColor: Colors[theme ?? 'light'].secondBackground
                }}
            >
                <Info
                    icon={'fas fa-circle-info'}
                    info={Strings[language ?? 'tm'].trashProfileDataTitle}
                    requestI={Strings[language ?? 'tm'].trashProfileDataI}
                />

                {step === 1 && <>
                    <MiniSwitch
                        icon='fas fa-mobile'
                        title={Strings[language ?? 'tm'].sessions}
                        value={sessions}
                        setValue={onChangeSessions}
                    />
                    <MiniSwitch
                        icon='fas fa-location-dot'
                        title={Strings[language ?? 'tm'].addresses}
                        value={addresses}
                        setValue={onChangeAddresses}
                    />
                    <MiniSwitch
                        icon='fas fa-heart'
                        title={Strings[language ?? 'tm'].myFavorites}
                        value={likes}
                        setValue={onChangeLikes}
                    />
                    <MiniSwitch
                        icon='fas fa-star'
                        title={Strings[language ?? 'tm'].myRates}
                        value={rates}
                        setValue={onChangeRates}
                    />
                    <PrevNextChanger
                        title={Strings[language ?? 'tm'].chooseData}
                        leftOnClick={() => onChangeStep(1)}
                        leftDisabled={true}
                        rightOnClick={() => onChangeStep(2)}
                        rightDisabled={
                            !sessions
                            && !addresses
                            && !likes
                            && !rates
                        }
                    />
                </>}

                {step === 2 && <>
                    <div
                        className={style.smallFormContainer}
                    >
                        <div
                            className={style.titleContainer}
                        >
                            <h1
                                className={style.title}
                                style={{
                                    color: Colors[theme ?? 'light'].text
                                }}
                            >
                                <i className={"fas fa-phone"}></i> {Strings[language ?? 'tm'].phone}
                            </h1>
                        </div>
                        <div
                            className={style.form}
                        >
                            <SendPhoneForm
                                initialValues={identifierInitialValues}
                                validate={identifierValidate}
                                onSubmit={identifierPhoneOnSubmit}
                            />
                        </div>
                    </div>
                    <PrevNextChanger
                        title={Strings[language ?? 'tm'].sendPhone}
                        leftOnClick={() => onChangeStep(1)}
                        leftDisabled={false}
                        rightOnClick={() => onChangeStep(3)}
                        rightDisabled={
                            !identified
                        }
                    />
                </>}

                {step === 3 && <>
                    <div
                        className={style.smallFormContainer}
                    >
                        <div
                            className={style.titleContainer}
                        >
                            <h1
                                className={style.title}
                                style={{
                                    color: Colors[theme ?? 'light'].text
                                }}
                            >
                                <i className={"fas fa-envelope"}></i> {Strings[language ?? 'tm'].otpPlace}
                            </h1>
                        </div>
                        <div
                            className={style.form}
                        >
                            <SendCodeForm
                                initialValues={removeDataInitialValues}
                                validate={removeDataValidate}
                                onSubmit={removeDataOnSubmit}
                            />
                        </div>
                    </div>
                    <PrevNextChanger
                        title={Strings[language ?? 'tm'].confirmPhone}
                        leftOnClick={() => {
                            onChangeStep(2)
                            dispatch(setIdentified(false))
                        }}
                        leftDisabled={false}
                        rightOnClick={() => onChangeStep(3)}
                        rightDisabled={true}
                    />
                </>}
            </div>
        </div>
    )
}

export default RemoveData